/**
 * Motivational Quotes Service
 * Generates psychologically-optimized motivational quotes based on exam performance
 * Uses psychological triggers to encourage engagement and future attempts
 */

interface QuoteSet {
  low: string[];      // 0-30%: Major failure - need encouragement
  medium: string[];   // 30-60%: Partial success - build confidence
  nearPass: string[]; // 60-74%: So close - create urgency
  pass: string[];     // 75%+: Success - celebrate & encourage
}

const motivationalQuotes: QuoteSet = {
  low: [
    "🌱 Every expert started as a beginner. This is your beginning!",
    "💪 Failure is the best teacher. You're learning what matters most!",
    "🚀 The fact that you tried puts you ahead of those who didn't. Keep going!",
    "🔥 Champions are built through resilience, not perfection. Try again!",
    "⭐ Success isn't final, failure isn't fatal. What matters is you keep moving!",
    "🎯 You're closer than you think. Every attempt brings you closer to mastery!",
    "💡 The next attempt is where breakthroughs happen. Don't stop now!",
  ],
  medium: [
    "🌟 You're getting there! You've got the foundation—now build on it!",
    "📈 Solid progress! You understand more than you think. Keep building!",
    "🎓 You're in the learning zone. Small tweaks will push you over the top!",
    "💪 Your effort is showing! This score proves you have what it takes!",
    "🔑 You've unlocked key concepts. Just a bit more to master them all!",
    "🚀 Momentum is on your side. One more push and you'll pass!",
    "⚡ You're closer to mastery than you realize. Review, improve, dominate!",
  ],
  nearPass: [
    "🎯 SO CLOSE! You're just steps away from mastery! One more attempt!",
    "🔥 You almost have it! Review those few questions and come back stronger!",
    "⭐ 75% is right there! You've got this—just one more focused attempt!",
    "💡 This is the moment! Champions don't give up at the finish line!",
    "🚀 You're THIS close to proving your expertise. One more shot!",
    "🌟 The breakthrough is coming! Push through this one more time!",
    "💪 Your mastery is within reach. Don't stop now!",
  ],
  pass: [
    "🏆 CONGRATULATIONS! You've proven your expertise!",
    "⭐ YOU DID IT! You're officially a certified expert!",
    "🎉 SUCCESS! You've mastered this roadmap!",
    "🚀 AMAZING! You're crushing it! Welcome to the elite!",
    "💎 BRILLIANT! Your skills are now officially validated!",
    "👑 EXCEPTIONAL! You've earned this credential!",
    "🌟 OUTSTANDING! You're exactly where you need to be!",
  ],
};

/**
 * Get a psychological quote based on score performance
 * Uses performance level to select appropriate motivational trigger
 */
export function getMotivationalQuote(scorePercentage: number): {
  quote: string;
  category: 'low' | 'medium' | 'nearPass' | 'pass';
} {
  let category: 'low' | 'medium' | 'nearPass' | 'pass';
  
  if (scorePercentage >= 75) {
    category = 'pass';
  } else if (scorePercentage >= 60) {
    category = 'nearPass';
  } else if (scorePercentage >= 30) {
    category = 'medium';
  } else {
    category = 'low';
  }

  const quotes = motivationalQuotes[category];
  const randomIndex = Math.floor(Math.random() * quotes.length);
  return {
    quote: quotes[randomIndex],
    category,
  };
}

/**
 * Get psychological encouragement message based on performance
 */
export function getEncouragementMessage(
  scorePercentage: number,
  correctAnswers: number,
  totalQuestions: number,
  attemptNumber: number
): string {
  if (scorePercentage >= 75) {
    return `You've earned your credential! Share this achievement with your network 🎓`;
  } else if (scorePercentage >= 60) {
    return `You're ${(75 - scorePercentage).toFixed(1)}% away from passing. You can do this! 💪`;
  } else if (scorePercentage >= 30) {
    return `You've got ${correctAnswers} right! Review those concepts and try again. 📚`;
  } else {
    return `This is your chance to learn! Review the material and come back stronger. 🔥`;
  }
}

/**
 * Get attempt status message
 */
export function getAttemptsMessage(
  remainingAttempts: number,
  maxAttempts: number = 3
): string {
  if (remainingAttempts === 0) {
    return `You've used all ${maxAttempts} attempts. Come back after reviewing the roadmap.`;
  } else if (remainingAttempts === 1) {
    return `⚠️ This is your last attempt! Review the material carefully.`;
  } else {
    return `You have ${remainingAttempts} attempts remaining.`;
  }
}
